/*
 * Shared handling for API errors. See docs/error-handling-plan.md.
 *
 * The API reports failures with HTTP 200 and a body of
 * { status, error: true, errorType, errorMessage }.
 *
 * assertApiSuccess() turns any non-SUCCESS body into a thrown ApiError, so API errors,
 * HTTP errors and timeouts all end up in the same .catch().
 *
 * showApiError() logs the error and returns a translated message: "api-error-<errortype>"
 * when that key exists, otherwise the caller's fallback key. Unless `silent` is set it also
 * shows the message in a dialog.
 *
 * Usage:
 *   this.$cfetch(url, options)
 *       .then(this.$assertApiSuccess)
 *       .then(res => { ... })
 *       .catch(err => this.$showApiError(err, { fallback: 'error-posting-reflection' }))
 *
 *   // inline error instead of a dialog
 *   .catch(err => { this.error = this.$showApiError(err, { fallback: 'failed-load-templates', silent: true }) })
 *
 *   // business codes first, then the generic handling
 *   .catch(err => {
 *       if (this.$apiErrorType(err) === 'EVENT_CONFLICT') return this.resolveConflict()
 *       this.$showApiError(err, { fallback: 'error-saving-event' })
 *   })
 */

export class ApiError extends Error {
	constructor(data) {
		super((data && data.errorMessage) || 'Unexpected API response');
		this.name = 'ApiError';
		this.data = data;
	}
}

export function assertApiSuccess(res) {
	if (!res || res.status !== 'SUCCESS') throw new ApiError(res);
	return res;
}

export function apiErrorType(err) {
	if (err && err.data && err.data.errorType) return err.data.errorType;
	if (err && err.timedOut) return 'TIMEOUT';
	return null;
}

// Called as a component method (Vue.prototype.$showApiError), so `this` is the component.
// Returns the message shown, or null for authentication failures.
export function showApiError(err, { fallback = 'something-went-wrong', onClose = () => {}, silent = false } = {}) {
	const data = (err && err.data) || null;
	const errorType = apiErrorType(err);

	this.$log.error.call(this, data || (err && err.message) || err);

	// cfetch has already logged the user out
	if (errorType === 'AUTHENTICATION_FAILED') return null;

	let content;
	if (errorType === 'MAINTENANCE_MODE' && data.errorMessage) {
		// configurable message set by the server
		content = data.errorMessage;
	} else {
		const key = errorType && `api-error-${errorType.toLowerCase().replace(/[\s_]+/g, '-')}`;
		content = key && this.$te(key) ? this.$t(key) : this.$t(fallback);
	}

	if (!silent) {
		this.$store.commit('openDialog', {
			title: this.$t('something-went-wrong'),
			content,
			confirmCallBack: onClose,
		});
	}

	return content;
}

export default {
	install(Vue) {
		Vue.prototype.$assertApiSuccess = assertApiSuccess;
		Vue.prototype.$showApiError = showApiError;
		Vue.prototype.$apiErrorType = apiErrorType;
	},
};
